import { formatCurrency, formatInteger } from '@/utils/numberFormat';
import { formatDateShort } from '@/utils/datePresentation';
import { CUSTOMERS_TEXT } from '@/texts/catalog/customers';
import { toAmount } from '@/utils/numberValue';
import { extractPayload } from '@/utils/dataNormalization';
import { buildRouteDetailLocation } from '@/composables/shared/useNavigation';

const buildLatestCustomerLabel = (customer = {}) => {
  const id = Number(customer?.id || 0);
  const name = String(customer?.name || '').trim();

  if (id <= 0 || name === '') {
    return CUSTOMERS_TEXT.metrics.latestCustomerEmpty;
  }

  return name;
};

const buildLatestCustomerMeta = (customer = {}) => {
  return `${CUSTOMERS_TEXT.metrics.latestCustomerMetaPrefix} ${formatDateShort(customer?.created_at)}`;
};

const buildLatestCustomerRoute = (customer = {}) => {
  return buildRouteDetailLocation('customerDetail', 'customerId', customer?.id);
};

export const createInitialCustomersMetricsValues = () => ({
  active_customers: 0,
  sales_in_progress: 0,
  income_forecast: 0,
  latest_customer: {
    id: 0,
    name: '',
    created_at: null,
  },
});

export const createInitialCustomersMetricsVisibility = () => ({
  can_view_sales: true,
  can_view_total: true,
});

export const mapCustomersMetricsPayload = (payload) => {
  return {
    values: {
      active_customers: toAmount(payload?.active_customers),
      sales_in_progress: toAmount(payload?.sales_in_progress),
      income_forecast: toAmount(payload?.income_forecast),
      latest_customer: {
        id: toAmount(payload?.latest_customer?.id),
        name: String(payload?.latest_customer?.name || '').trim(),
        created_at: payload?.latest_customer?.created_at || null,
      },
    },
    visibility: {
      can_view_sales: payload?.capabilities?.can_view_sales !== false,
      can_view_total: payload?.capabilities?.can_view_total !== false,
    },
  };
};

export const fetchCustomersMetricsPayload = async (customersService, params = {}) => {
  const response = await customersService.metrics(params);
  const payload = extractPayload(response);
  return mapCustomersMetricsPayload(payload);
};

export const buildCustomersKpiCards = (metricsValues, metricsVisibility) => {
  const cards = [];

  if (metricsVisibility?.can_view_total !== false) {
    cards.push({
      title: CUSTOMERS_TEXT.metrics.incomeForecast,
      value: formatCurrency(metricsValues?.income_forecast),
      icon: 'fa-money',
      iconBg: 'rgba(0, 178, 226, 0.12)',
      iconColor: '#00b2e2',
    });
  }

  cards.push({
    title: CUSTOMERS_TEXT.metrics.activeCustomers,
    value: formatInteger(metricsValues?.active_customers),
    icon: 'fa-users',
    iconBg: 'rgba(0, 112, 205, 0.12)',
    iconColor: '#0070cd',
  });

  if (metricsVisibility?.can_view_sales !== false) {
    cards.push({
      title: CUSTOMERS_TEXT.metrics.salesInProgress,
      value: formatInteger(metricsValues?.sales_in_progress),
      icon: 'fa-tasks',
      iconBg: 'rgba(77, 191, 180, 0.12)',
      iconColor: '#4dbfb4',
    });
  }

  cards.push({
    title: CUSTOMERS_TEXT.metrics.latestCustomer,
    value: buildLatestCustomerLabel(metricsValues?.latest_customer),
    valueRoute: buildLatestCustomerRoute(metricsValues?.latest_customer),
    valueLinkClass: 'kpi-value-link-customer',
    valueMeta: buildLatestCustomerMeta(metricsValues?.latest_customer),
    truncateValue: true,
    valueScale: 0.5625,
    icon: 'fa-user-plus',
    iconBg: 'rgba(1, 191, 210, 0.12)',
    iconColor: '#01bfd2',
  });

  return cards;
};
